/**
 * Every URL that can carry a completed Stripe checkout session.
 *
 * | Param                      | Where it appears                                |
 * | -------------------------- | ----------------------------------------------- |
 * | `session_id`               | Stripe return (all order-confirmation pages)    |
 * | `original_product_session` | `/[locale]/[crossSellProduct]/offer`            |
 * | `previous_session_id`      | Order confirmation after a one-click cross-sell |
 */
import { type CheckoutCountry, CHECKOUT_COUNTRIES } from './types'

export const PURCHASE_SESSION_PARAM_KEYS = ['previous_session_id', 'session_id', 'original_product_session'] as const

const COUNTRY_CODE_PARAM = 'country_code' as const

/** Session ids on the URL, earliest first. The same id can appear under two param names. */
export const extractPurchaseSessionIds = (params: URLSearchParams | null | undefined): string[] => {
  if (!params) return []
  // `previous_session_id` repeats when cross-sell offers are chained.
  const ids = PURCHASE_SESSION_PARAM_KEYS.flatMap((key) => params.getAll(key).map((id) => id.trim())).filter(
    (id) => id.length > 0,
  )
  return Array.from(new Set(ids))
}

const isCheckoutCountry = (value: string): value is CheckoutCountry =>
  (CHECKOUT_COUNTRIES as readonly string[]).includes(value)

/** `country_code` is always on these routes; the locale is a fallback for hand-built links. */
export const resolvePurchaseCountryCode = (
  params: URLSearchParams | null | undefined,
  localeCountryCode?: string,
): CheckoutCountry | null => {
  const fromParam = params?.get(COUNTRY_CODE_PARAM)?.trim().toUpperCase()
  if (fromParam && isCheckoutCountry(fromParam)) return fromParam
  const fromLocale = localeCountryCode?.trim().toUpperCase()
  if (fromLocale && isCheckoutCountry(fromLocale)) return fromLocale
  return null
}
